import { createSignal, For } from "solid-js";
import type { OrganizationMember } from "../../shared";
import { UserRow } from "./UserRow";

export interface EditData {
  userId: string;
  originalUser: any;
  originalRole: string;
  originalMemberStatus?: string;
  newRole: string;
  newMemberStatus: string;
  newUserStatus: string;
}

interface UsersTabProps {
  users: any[];
  members: OrganizationMember[];
  orgId: string;
  roleFilter: string;
  authFilter: "" | "authenticated" | "anonymous";
  onRoleFilterChange: (role: string) => void;
  onAuthFilterChange: (auth: "" | "authenticated" | "anonymous") => void;
  onCreateUser: () => void;
  onAddMember: () => void;
  onSaveEdit: (data: EditData) => Promise<void>;
  onRemoveMember: (userId: string) => void;
  onLoginLink: (userId: string, email: string) => void;
  onSetPw: (userId: string, email: string) => void;
  onVerifyEmail: (userId: string, email: string) => void;
  onVerifyEmailDirect: (userId: string) => void;
  onUnverifyEmail: (userId: string) => void;
}

export function UsersTab(props: UsersTabProps) {
  const [expandedUserId, setExpandedUserId] = createSignal<string | null>(null);
  const [editingUserId, setEditingUserId] = createSignal<string | null>(null);
  const [editRole, setEditRole] = createSignal("");
  const [editMemberStatus, setEditMemberStatus] = createSignal("");
  const [editUserStatus, setEditUserStatus] = createSignal("");

  function startEdit(user: any, memberRole: string, memberStatus?: string) {
    setEditingUserId(user.id);
    setEditRole(memberRole || "");
    setEditMemberStatus(memberStatus || "approved");
    setEditUserStatus(user.status);
  }

  function cancelEdit() {
    setEditingUserId(null);
    setEditRole("");
    setEditMemberStatus("");
    setEditUserStatus("");
  }

  async function saveEdit(userId: string, originalUser: any, originalRole: string, originalMemberStatus?: string) {
    await props.onSaveEdit({
      userId,
      originalUser,
      originalRole,
      originalMemberStatus,
      newRole: editRole(),
      newMemberStatus: editMemberStatus(),
      newUserStatus: editUserStatus(),
    });
    setEditingUserId(null);
  }

  return (
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Users ({props.users.length})</h3>
        <div class="flex gap-2">
          <button onClick={props.onCreateUser} class="text-xs px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Create User</button>
          <button onClick={props.onAddMember} class="text-xs px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Add Member</button>
        </div>
      </div>
      <div class="flex flex-wrap gap-2">
        <div class="flex gap-1 bg-gray-900 rounded-lg p-1 w-fit">
          {(["", "owner", "manager", "searcher"] as const).map((r) => (
            <button onClick={() => props.onRoleFilterChange(r)} class={`px-3 py-1 text-xs rounded transition-colors cursor-pointer ${props.roleFilter === r ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>
              {r === "" ? "All" : r}
            </button>
          ))}
        </div>
        <div class="flex gap-1 bg-gray-900 rounded-lg p-1 w-fit">
          {(["", "authenticated", "anonymous"] as const).map((a) => (
            <button onClick={() => props.onAuthFilterChange(a)} class={`px-3 py-1 text-xs rounded transition-colors cursor-pointer ${props.authFilter === a ? "bg-gray-700 text-white" : "text-gray-400 hover:text-gray-200"}`}>
              {a === "" ? "All" : a === "authenticated" ? "Authenticated" : "Anonymous"}
            </button>
          ))}
        </div>
      </div>
      <div class="border border-gray-800 rounded-lg overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-900 text-gray-400">
            <tr>
              <th class="text-left px-4 py-3 font-medium">Email</th>
              <th class="text-left px-4 py-3 font-medium">Display Name</th>
              <th class="text-left px-4 py-3 font-medium">Email Verification</th>
              <th class="text-left px-4 py-3 font-medium">Role</th>
              <th class="text-left px-4 py-3 font-medium">Org Status</th>
              <th class="text-left px-4 py-3 font-medium">User Status</th>
              <th class="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800">
            <For each={props.users}>{(u: any) => {
              const member = () => props.members.find((m) => m.userId === u.id);
              const memberRole = () => member()?.role;
              const memberStatus = () => member()?.status;
              const isOwner = () => memberRole() === "owner";
              const providers: string[] = (u as any).providers ?? [];
              const authLabel = providers.length === 1 ? providers[0] : providers.length > 1 ? "multiple" : "none";

              return (
                <UserRow
                  user={u}
                  memberRole={memberRole()}
                  memberStatus={memberStatus()}
                  isOwner={isOwner()}
                  isExpanded={expandedUserId() === u.id}
                  isEditing={editingUserId() === u.id}
                  editRole={editRole()}
                  editMemberStatus={editMemberStatus()}
                  editUserStatus={editUserStatus()}
                  providers={providers}
                  authLabel={authLabel}
                  onExpand={() => setExpandedUserId(expandedUserId() === u.id ? null : u.id)}
                  onStartEdit={() => startEdit(u, memberRole() ?? "", memberStatus())}
                  onSave={() => saveEdit(u.id, u, memberRole() ?? "", memberStatus())}
                  onCancel={cancelEdit}
                  onEditRoleChange={setEditRole}
                  onEditMemberStatusChange={setEditMemberStatus}
                  onEditUserStatusChange={setEditUserStatus}
                  onLoginLink={() => props.onLoginLink(u.id, u.email)}
                  onSetPw={() => props.onSetPw(u.id, u.email)}
                  onVerifyEmail={() => props.onVerifyEmail(u.id, u.email)}
                  onVerifyEmailDirect={() => props.onVerifyEmailDirect(u.id)}
                  onUnverifyEmail={() => props.onUnverifyEmail(u.id)}
                  onRemove={() => props.onRemoveMember(u.id)}
                />
              );
            }}</For>
          </tbody>
        </table>
      </div>
    </div>
  );
}
